import * as THREE from 'three';
import { splitWall } from '../build.js';
import { createBuilder } from './builder.js';
import { createSignAtlas, paintCards, paintDial, paintLockers, paintSheet } from './signs.js';
import { roomIndex } from './rooms.js';

/**
 * The set dressing: trim, door frames, signs, and the few things in the
 * building that are more than a box — the BEO board, the time clock, the
 * kitchen pass, the housekeeping carts.
 *
 * Visual only, all of it. Nothing here is collided with and nothing here adds
 * a collider: where a thing stands on the floor, it stands inside a prop's box
 * that was already there (and that box is still what you bump into), or it is
 * trim a few millimetres proud of a wall, or paint on the ground. Where this
 * draws a prop in detail it hides that prop's plain box; the box's collision
 * is elsewhere and does not notice.
 */

const FT = 0.3048;
const m = (feet) => feet * FT;

/** How each kind of room finishes its walls at the floor, and at the top. */
function trimOf(room) {
  if (!room || !room.ceiling) return null;
  if (room.zone === 'event') return null; // The ballroom does its own; see ballroom.js.
  if (room.zone === 'foh') return { base: 0.15, baseColour: 0x3a3430, crown: 0.22, crownColour: 0xe6dfd2 };
  if (room.id === 'office') return { base: 0.1, baseColour: 0x5a4030 };
  if (room.id === 'kitchen') return { base: 0.13, baseColour: 0x7a3a2a };
  // Back of house: black rubber cove base, and in the two routes the carts
  // run down, a crash rail at bumper height.
  return { base: 0.1, baseColour: 0x1c1c1c, rail: room.id === 'corridor' || room.id === 'run' };
}

/** What a door frame is made of, on the side you see it from. */
function frameOf(room) {
  if (!room) return null;
  if (!room.ceiling) return 0x4a4e54;
  // Brass in front of house, as the property reference has it.
  if (room.zone === 'foh' || room.zone === 'event') return 0xc9a35b;
  return 0x55606c;
}

export function createDressing({ plan, levelObject, environment = null }) {
  const group = new THREE.Group();
  group.name = 'look-dressing';
  const roomAt = roomIndex(plan.rooms);
  const zones = plan.zones ?? [];
  const restricted = (xm, zm) =>
    zones.some((z) => z.kind === 'restricted' && xm >= Math.min(z.rect[0], z.rect[2]) * FT && xm <= Math.max(z.rect[0], z.rect[2]) * FT && zm >= Math.min(z.rect[1], z.rect[3]) * FT && zm <= Math.max(z.rect[1], z.rect[3]) * FT);
  const b = createBuilder();
  const atlas = createSignAtlas();
  const disposables = [atlas.texture];

  const materials = new Map();
  const paint = (colour, extra = {}) => {
    const key = `${colour}|${JSON.stringify(extra)}`;
    if (!materials.has(key)) {
      const made = new THREE.MeshStandardMaterial({ color: colour, roughness: 0.7, ...extra });
      if (extra.metalness && environment) made.envMap = environment;
      materials.set(key, made);
      disposables.push(made);
    }
    return materials.get(key);
  };
  const signs = new THREE.MeshStandardMaterial({ map: atlas.texture, roughness: 0.55 });
  const glowing = new THREE.MeshStandardMaterial({ map: atlas.texture, emissiveMap: atlas.texture, emissive: 0xffffff, emissiveIntensity: 1.4, roughness: 0.5 });
  disposables.push(signs, glowing);

  /**
   * A strip laid along one face of a wall: `lo`..`hi` feet along the wall's
   * axis, `y0`..`y1` metres up, `depth` metres proud of the face.
   */
  function strip(material, face, lo, hi, y0, y1, depth) {
    const along = m((lo + hi) / 2);
    const across = m(face.cross) + face.side * depth / 2;
    const len = m(hi - lo);
    if (face.horizontal) b.box(material, along, (y0 + y1) / 2, across, len, y1 - y0, depth);
    else b.box(material, across, (y0 + y1) / 2, along, depth, y1 - y0, len);
  }
  /** A sign on a wall face, centred `at` feet along it, `y` metres up. */
  function signOn(face, at, y, width, height, uv, material = signs) {
    const along = m(at);
    const across = m(face.cross) + face.side * 0.014;
    const normal = face.horizontal ? [0, face.side] : [face.side, 0];
    if (face.horizontal) b.panel(material, along, y, across, width, height, normal, uv);
    else b.panel(material, across, y, along, width, height, normal, uv);
  }

  // --- trim along every wall ---------------------------------------------------
  for (const wall of plan.walls) {
    const [x0, z0] = wall.from;
    const [x1, z1] = wall.to;
    const horizontal = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    const half = (wall.thickness ?? 0.5) / 2;
    const cross = horizontal ? z0 : x0;
    for (const piece of splitWall({ thickness: 0.5, ...wall })) {
      if (piece.min.y > 0) continue;
      const lo = horizontal ? piece.min.x : piece.min.z;
      const hi = horizontal ? piece.max.x : piece.max.z;
      for (const side of [1, -1]) {
        const mid = (lo + hi) / 2;
        const probe = horizontal ? roomAt(m(mid), m(cross + side * (half + 1))) : roomAt(m(cross + side * (half + 1)), m(mid));
        const trim = trimOf(probe);
        if (!trim) continue;
        const face = { horizontal, cross: cross + side * half, side };
        strip(paint(trim.baseColour, { roughness: 0.5 }), face, lo, hi, 0, trim.base, 0.014);
        if (trim.crown) {
          const top = m(probe.ceiling);
          strip(paint(trim.crownColour), face, lo, hi, top - trim.crown, top, 0.06);
        }
        if (trim.rail) {
          strip(paint(0x7a7e84, { roughness: 0.45 }), face, lo, hi, 0.72, 0.88, 0.05);
          strip(paint(0x4a4e54), face, lo, hi, 0.7, 0.72, 0.03);
        }
      }
    }
  }

  // --- doors: frames, roll-up gear, and the signs beside them ------------------
  const framed = new Set();
  for (const wall of plan.walls) {
    const [x0, z0] = wall.from;
    const [x1, z1] = wall.to;
    const horizontal = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    const start = horizontal ? x0 : z0;
    const end = horizontal ? x1 : z1;
    const sign = Math.sign(end - start) || 1;
    const half = (wall.thickness ?? 0.5) / 2;
    const cross = horizontal ? z0 : x0;
    for (const door of wall.doors ?? []) {
      const a = start + sign * door.at;
      const c = start + sign * (door.at + door.width);
      const lo = Math.min(a, c);
      const hi = Math.max(a, c);
      const key = `${horizontal}|${Math.round(cross)}|${Math.round(lo)}`;
      if (framed.has(key)) continue;
      framed.add(key);
      const height = Math.min(door.height ?? 7, wall.height ?? 9);
      const full = height >= (wall.height ?? 9) - 0.01;
      const mid = (lo + hi) / 2;
      const sideRoom = (side) => (horizontal ? roomAt(m(mid), m(cross + side * (half + 2))) : roomAt(m(cross + side * (half + 2)), m(mid)));

      for (const side of [1, -1]) {
        const here = sideRoom(side);
        const there = sideRoom(-side);
        const colour = frameOf(here);
        if (!colour) continue;
        const face = { horizontal, cross: cross + side * half, side };
        const frame = paint(colour, { roughness: 0.4, metalness: colour === 0xc9a35b ? 0.8 : 0.3 });
        const h = m(height);
        if (full) {
          // A roll-up door, open: its drum across the top, its guides down the sides.
          if (here?.ceiling) {
            strip(paint(0x6a6e74, { metalness: 0.5, roughness: 0.4 }), face, lo - 0.3, hi + 0.3, h - 0.55, h - 0.02, 0.5);
            strip(paint(0x3a3e44, { metalness: 0.4 }), face, lo - 0.45, lo, 0, h - 0.55, 0.12);
            strip(paint(0x3a3e44, { metalness: 0.4 }), face, hi, hi + 0.45, 0, h - 0.55, 0.12);
          }
        } else {
          strip(frame, face, lo - 0.4, lo, 0, h + 0.1, 0.035);
          strip(frame, face, hi, hi + 0.4, 0, h + 0.1, 0.035);
          strip(frame, face, lo - 0.4, hi + 0.4, h, h + 0.13, 0.035);
        }

        // What the door says, on this side.
        const room = here;
        if (!room) continue;
        const ceilingM = room.ceiling ? m(room.ceiling) : Infinity;
        const wallTop = m(wall.height ?? 9);
        const above = Math.min(h + 0.32, ceilingM - 0.2, wallTop - 0.15);
        const besideAt = hi + 1.6 + 0.25;
        const prefix = there?.id === 'apron' ? 'AUTHORIZED ONLY' : 'PRIVATE';
        const farTo = there && restricted(horizontal ? m(mid) : m(cross - side * (half + 6)), horizontal ? m(cross - side * (half + 6)) : m(mid));
        if (room.ceiling && there && !there.ceiling) {
          if (above > h + 0.1) signOn(face, mid, above, 0.75, 0.19, atlas.sign('EXIT', 'exit'), glowing);
        } else if (there && room.zone === 'foh' && there.zone === 'event') {
          if (above > h + 0.2) signOn(face, mid, Math.min(h + 0.7, ceilingM - 0.6), 2.8, 0.7, atlas.sign('CROWN BALLROOM', 'foh'));
        } else if (there && (room.zone === 'foh' || room.zone === 'event') && there.zone !== 'foh' && there.zone !== 'event') {
          signOn(face, besideAt, 1.55, 0.9, 0.225, atlas.sign('EMPLOYEES ONLY', 'warning'));
        } else if (there && !room.ceiling) {
          const text = there.name.toUpperCase();
          const style = farTo ? 'warning' : 'outdoor';
          signOn(face, besideAt, 1.75, 1.2, 0.3, atlas.sign(farTo ? `${prefix} · ${text}` : text, style));
        } else if (there) {
          const text = there.name.toUpperCase();
          signOn(face, besideAt, 1.55, 0.8, 0.2, atlas.sign(farTo ? `${prefix} · ${text}` : text, farTo ? 'warning' : room.zone === 'event' ? 'foh' : 'plate'));
        }
      }
    }
  }

  // --- the named things ----------------------------------------------------------
  const props = new Map(plan.props.map((p) => [p.id, p]));
  const box = (p) => {
    const [ax, az, bx, bz] = p.rect;
    return {
      minX: m(Math.min(ax, bx)),
      maxX: m(Math.max(ax, bx)),
      minZ: m(Math.min(az, bz)),
      maxZ: m(Math.max(az, bz)),
      base: m(p.base ?? 0),
      top: m((p.base ?? 0) + p.height),
    };
  };
  const hide = (id) => {
    const mesh = levelObject.getObjectByName(`prop:${id}`);
    if (mesh) mesh.visible = false;
  };
  /**
   * Which way a thing against a wall faces: across its short side (a bank of
   * lockers faces out of its length, not along it), toward whichever side has
   * the room in it.
   */
  const facingOf = (r) => {
    const cx = (r.minX + r.maxX) / 2;
    const cz = (r.minZ + r.maxZ) / 2;
    const longX = r.maxX - r.minX >= r.maxZ - r.minZ;
    const out = (nx, nz) => {
      let free = 0;
      const ex = nx > 0 ? r.maxX : nx < 0 ? r.minX : cx;
      const ez = nz > 0 ? r.maxZ : nz < 0 ? r.minZ : cz;
      for (let d = 0.3; d < 2; d += 0.3) if (roomAt(ex + nx * d, ez + nz * d)) free += 1;
      return free;
    };
    return longX ? (out(0, 1) >= out(0, -1) ? [0, 1] : [0, -1]) : out(1, 0) >= out(-1, 0) ? [1, 0] : [-1, 0];
  };

  // The BEO board: a cork board in an aluminium frame, with the week's orders
  // pinned up in rows.
  const beo = props.get('beo-board');
  if (beo) {
    const r = box(beo);
    hide(beo.id);
    // It hangs on the alcove's east wall and faces west, into the alcove.
    const x = r.maxX - 0.05;
    const zc = (r.minZ + r.maxZ) / 2;
    const width = r.maxZ - r.minZ - 0.1;
    const height = r.top - r.base;
    const yc = (r.base + r.top) / 2;
    b.box(paint(0x8a6a44, { roughness: 0.95 }), x, yc, zc, 0.04, height - 0.1, width - 0.1);
    b.box(paint(0xb4b8bc, { metalness: 0.6, roughness: 0.35 }), x - 0.01, r.top - 0.03, zc, 0.06, 0.05, width);
    b.box(paint(0xb4b8bc, { metalness: 0.6, roughness: 0.35 }), x - 0.01, r.base + 0.03, zc, 0.06, 0.05, width);
    b.box(paint(0xb4b8bc, { metalness: 0.6, roughness: 0.35 }), x - 0.01, yc, r.minZ + 0.08, 0.06, height, 0.05);
    b.box(paint(0xb4b8bc, { metalness: 0.6, roughness: 0.35 }), x - 0.01, yc, r.maxZ - 0.08, 0.06, height, 0.05);
    b.panel(signs, x - 0.03, r.top + 0.16, zc, width * 0.8, 0.2, [-1, 0], atlas.sign('BANQUET EVENT ORDERS', 'header'));
    // Three rows of orders, eight to a row, each with a red pin.
    for (let row = 0; row < 3; row += 1) {
      for (let half = 0; half < 2; half += 1) {
        const w = (width - 0.3) / 2;
        const z = r.minZ + 0.2 + w * (half + 0.5);
        const y = r.top - 0.3 - row * 0.36;
        b.panel(signs, x - 0.03, y, z, w, w / 4, [-1, 0], atlas.custom(`sheets${row * 2 + half}`, paintSheet(row * 2 + half)));
        for (let k = 0; k < 4; k += 1) b.box(paint(0xc8302a, { roughness: 0.4 }), x - 0.045, y + w / 8 - 0.02, z - w / 2 + w / 8 + (k * w) / 4, 0.02, 0.02, 0.02);
      }
    }
  }

  // The time clock: a grey punch clock whose hands tell the time, with a rack
  // of cards either side.
  let hands = null;
  const clockProp = props.get('time-clock');
  if (clockProp) {
    const r = box(clockProp);
    hide(clockProp.id);
    // On the alcove's west wall, facing east.
    const x = r.minX + 0.02;
    const zc = (r.minZ + r.maxZ) / 2;
    b.box(paint(0x9ea7b2, { roughness: 0.5, metalness: 0.2 }), x + 0.1, r.base + 0.28, zc, 0.2, 0.46, 0.34);
    // The dial: the square left end of its cell, so the hands have somewhere to sit.
    const dialCell = atlas.custom('dial', paintDial);
    const du = dialCell[0] + (dialCell[2] - dialCell[0]) * 0.25;
    b.panel(signs, x + 0.202, r.base + 0.38, zc, 0.2, 0.2, [1, 0], [dialCell[0], dialCell[1], du, dialCell[3]]);
    for (const sideZ of [r.minZ + 0.12, r.maxZ - 0.12]) {
      b.box(paint(0x70767c, { metalness: 0.3 }), x + 0.03, r.base + 0.2, sideZ, 0.06, 0.4, 0.2);
      b.panel(signs, x + 0.062, r.base + 0.26, sideZ, 0.19, 0.28, [1, 0], atlas.custom('cards', paintCards));
    }
    b.panel(signs, x + 0.01, r.top + 0.14, zc, 0.6, 0.15, [1, 0], atlas.sign('TIME CLOCK', 'plate'));
    // Hands: separate, so they can turn.
    const handMaterial = paint(0x1a1a1a);
    const hour = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.05, 0.008).translate(0, 0.025, 0), handMaterial);
    const minute = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.075, 0.006).translate(0, 0.0375, 0), handMaterial);
    for (const hand of [hour, minute]) {
      hand.position.set(x + 0.206, r.base + 0.38, zc);
      group.add(hand);
      disposables.push(hand.geometry);
    }
    hands = { hour, minute };
  }

  // The kitchen pass: a heat-lamp gantry hung over the counter, tickets on the
  // rail, plates waiting under the lamps.
  const pass = props.get('kitchen-pass');
  if (pass) {
    const r = box(pass);
    const cx = (r.minX + r.maxX) / 2;
    const cz = (r.minZ + r.maxZ) / 2;
    const lampY = m(7.2);
    const steel = paint(0xb8bec4, { metalness: 0.7, roughness: 0.3 });
    b.box(steel, cx, lampY, cz, r.maxX - r.minX - 0.3, 0.08, 0.5);
    for (const dx of [-1, 1]) b.box(steel, cx + dx * ((r.maxX - r.minX) / 2 - 0.25), (lampY + m(12)) / 2, cz, 0.04, m(12) - lampY, 0.04);
    const lamps = Math.max(2, Math.round((r.maxX - r.minX) / 0.6));
    for (let i = 0; i < lamps; i += 1) {
      const x = r.minX + 0.3 + ((r.maxX - r.minX - 0.6) * (i + 0.5)) / lamps;
      b.box(paint(0x301008, { emissive: 0xff5a1e, emissiveIntensity: 1.8 }), x, lampY - 0.06, cz, 0.16, 0.03, 0.16);
    }
    // The ticket rail, and the tickets on it.
    b.box(steel, cx, lampY + 0.08, cz + 0.27, r.maxX - r.minX - 0.3, 0.04, 0.03);
    for (let i = 0; i < 7; i += 1) b.box(paint(0xf4f0e2, { roughness: 0.9 }), r.minX + 0.5 + i * 0.42, lampY - 0.02, cz + 0.29, 0.08, 0.16, 0.004);
    // Plates, and domes over two of them.
    for (let i = 0; i < 6; i += 1) {
      const x = r.minX + 0.5 + i * ((r.maxX - r.minX - 1) / 5);
      const plate = new THREE.CylinderGeometry(0.13, 0.11, 0.02, 20).translate(x, r.top + 0.01, cz + (i % 2 ? 0.2 : -0.2));
      b.add(paint(0xf8f8f4, { roughness: 0.25 }), plate);
      if (i % 3 === 1) b.add(steel, new THREE.SphereGeometry(0.12, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2).translate(x, r.top + 0.02, cz + 0.2));
    }
  }

  // The housekeeping carts: a steel frame, a canvas bag at one end, shelves of
  // folded linen, four casters.
  for (const id of ['hskp-cart-a', 'hskp-cart-b', 'hskp-cart-c']) {
    const p = props.get(id);
    if (!p) continue;
    const r = box(p);
    hide(id);
    const w = r.maxX - r.minX;
    const d = r.maxZ - r.minZ;
    const cx = (r.minX + r.maxX) / 2;
    const cz = (r.minZ + r.maxZ) / 2;
    const top = r.top;
    const frame = paint(0x9aa0a6, { metalness: 0.6, roughness: 0.35 });
    for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      b.box(frame, cx + dx * (w / 2 - 0.03), top / 2 + 0.05, cz + dz * (d / 2 - 0.03), 0.03, top - 0.1, 0.03);
      b.add(paint(0x222222), new THREE.CylinderGeometry(0.05, 0.05, 0.04, 12).rotateX(Math.PI / 2).translate(cx + dx * (w / 2 - 0.06), 0.05, cz + dz * (d / 2 - 0.06)));
    }
    // Shelves.
    for (const y of [0.18, 0.55, 0.92]) b.box(frame, cx + w * 0.15, y, cz, w * 0.68, 0.025, d - 0.04);
    // Linen: folded stacks on each shelf.
    const linen = paint(0xf2f0ea, { roughness: 0.95 });
    for (const y of [0.19, 0.56, 0.93]) {
      for (let i = 0; i < 3; i += 1) b.box(linen, cx - w * 0.12 + i * (w * 0.2), y + 0.07, cz, w * 0.17, 0.13, d * 0.7);
    }
    // The bag, hung off the end.
    b.box(paint(0x4a6a8a, { roughness: 1 }), cx - w / 2 + w * 0.17, top * 0.55, cz, w * 0.3, top * 0.8, d - 0.08);
    b.box(frame, cx, top - 0.02, cz, w, 0.03, d);
  }

  // The locker doors, on the lockers' face.
  const lockers = props.get('break-lockers');
  if (lockers) {
    const r = box(lockers);
    const [nx, nz] = facingOf(r);
    const cell = atlas.custom('lockers', paintLockers);
    const along = nx === 0;
    const span = along ? r.maxX - r.minX : r.maxZ - r.minZ;
    const height = r.top - r.base;
    const tile = m(6);
    const rows = 2;
    for (let row = 0; row < rows; row += 1) {
      for (let t = 0; t < Math.ceil(span / tile); t += 1) {
        const w = Math.min(tile, span - t * tile);
        const frac = w / tile;
        const uv = [cell[0], cell[1], cell[0] + (cell[2] - cell[0]) * frac, cell[3]];
        const start = (along ? r.minX : r.minZ) + t * tile + w / 2;
        const y = r.base + (height / rows) * (row + 0.5);
        const hgt = height / rows - 0.02;
        if (along) b.panel(signs, start, y, nz > 0 ? r.maxZ + 0.004 : r.minZ - 0.004, w - 0.01, hgt, [0, nz], uv);
        else b.panel(signs, nx > 0 ? r.maxX + 0.004 : r.minX - 0.004, y, start, w - 0.01, hgt, [nx, 0], uv);
      }
    }
  }

  // Range hoods over the kitchen's ranges, hung from its ceiling.
  for (const id of ['kitchen-range-a', 'kitchen-range-b']) {
    const p = props.get(id);
    if (!p) continue;
    const r = box(p);
    const steel = paint(0xb0b6bc, { metalness: 0.7, roughness: 0.35 });
    b.box(steel, (r.minX + r.maxX) / 2, m(8.2), (r.minZ + r.maxZ) / 2, r.maxX - r.minX + 0.2, m(1.6), r.maxZ - r.minZ + 0.3);
    b.box(steel, (r.minX + r.maxX) / 2, m(10.5), (r.minZ + r.maxZ) / 2, 0.6, m(3), 0.5);
  }

  // --- paint on the ground ---------------------------------------------------------
  const white = paint(0xecebe4, { roughness: 0.8 });
  const yellow = paint(0xe0b020, { roughness: 0.8 });
  // Parking stalls between and beside each lot's kerbs, nine feet wide: the
  // staff lot's, and the Caytons'.
  for (const [kerbA, kerbB] of [['lot-kerb-a', 'lot-kerb-b'], ['caytons-kerb-a', 'caytons-kerb-b']].map((pair) => pair.map((id) => props.get(id)))) {
    if (!kerbA || !kerbB) continue;
    const ka = box(kerbA);
    const kb = box(kerbB);
    const rows = [
      [ka.maxZ, ka.maxZ + m(18)],
      [kb.maxZ, kb.maxZ + m(18)],
    ];
    for (const [z0, z1] of rows) {
      for (let x = ka.minX; x <= ka.maxX + 0.01; x += m(9)) b.decal(white, x, (z0 + z1) / 2, 0.1, z1 - z0);
    }
  }
  // The dock's edge, in safety yellow, and the steps' nosings.
  const platform = props.get('dock-platform');
  if (platform) {
    const r = box(platform);
    b.decal(yellow, (r.minX + r.maxX) / 2, r.maxZ - 0.1, r.maxX - r.minX, 0.2, r.top + 0.006);
  }
  for (const p of plan.props.filter((q) => /-step-/.test(q.id))) {
    const r = box(p);
    b.decal(yellow, (r.minX + r.maxX) / 2, (r.minZ + r.maxZ) / 2, r.maxX - r.minX, 0.05, r.top + 0.006);
  }
  // The loading zone on the apron: a yellow box, hatched.
  const apron = plan.rooms.find((room) => room.id === 'apron');
  if (apron) {
    const x0 = m(2);
    const x1 = m(26);
    const z0 = m(-64);
    const z1 = m(-54);
    b.decal(yellow, (x0 + x1) / 2, z0, x1 - x0, 0.12);
    b.decal(yellow, (x0 + x1) / 2, z1, x1 - x0, 0.12);
    b.decal(yellow, x0, (z0 + z1) / 2, 0.12, z1 - z0);
    b.decal(yellow, x1, (z0 + z1) / 2, 0.12, z1 - z0);
    for (let k = x0 - (z1 - z0) + 1; k < x1; k += 1) {
      // A 45-degree line x - z = k - z0, clipped to the box.
      const ax = Math.max(x0, k);
      const bx = Math.min(x1, k + (z1 - z0));
      if (bx - ax < 0.2) continue;
      const len = (bx - ax) * Math.SQRT2;
      b.decal(yellow, (ax + bx) / 2, z0 + ((ax + bx) / 2 - k), 0.1, len, 0.007, Math.PI / 4);
    }
  }

  // The pickleball courts inside their fence: green surround, blue courts,
  // white lines, and the kitchen line seven feet either side of each net.
  const fence = ['court-fence-n', 'court-fence-s-w', 'court-fence-w', 'court-fence-e'].map((id) => props.get(id));
  if (fence.every(Boolean)) {
    const minX = box(fence[2]).maxX + 0.1;
    const maxX = box(fence[3]).minX - 0.1;
    const minZ = box(fence[0]).maxZ + 0.1;
    const maxZ = box(fence[1]).minZ - 0.1;
    b.decal(paint(0x3c6a4c, { roughness: 0.7 }), (minX + maxX) / 2, (minZ + maxZ) / 2, maxX - minX, maxZ - minZ, 0.006);
    for (const net of plan.props.filter((q) => /^court-.-net$/.test(q.id)).map(box)) {
      const cx = (net.minX + net.maxX) / 2;
      const cz = (net.minZ + net.maxZ) / 2;
      const w = m(20);
      const half = Math.min(m(22), (maxZ - minZ) / 4 - 0.3);
      b.decal(paint(0x2c5a8a, { roughness: 0.7 }), cx, cz, w, half * 2, 0.008);
      const line = (x, z, lw, ld) => b.decal(white, x, z, lw, ld, 0.01);
      line(cx - w / 2, cz, 0.05, half * 2);
      line(cx + w / 2, cz, 0.05, half * 2);
      line(cx, cz - half, w, 0.05);
      line(cx, cz + half, w, 0.05);
      line(cx, cz - m(7), w, 0.05);
      line(cx, cz + m(7), w, 0.05);
      line(cx, cz - (half + m(7)) / 2, 0.05, half - m(7));
      line(cx, cz + (half + m(7)) / 2, 0.05, half - m(7));
    }
  }

  // --- the lobby's name, over the desk -------------------------------------------
  const desk = props.get('lobby-desk');
  const lobby = plan.rooms.find((room) => room.zone === 'foh');
  if (desk && lobby) {
    const r = box(desk);
    const wallX = m(Math.max(lobby.rect[0], lobby.rect[2]));
    b.panel(signs, wallX - 0.02, m(12), (r.minZ + r.maxZ) / 2, 4.2, 1.05, [-1, 0], atlas.sign('THE LION & CROWN', 'foh'));
    /*
     * The lifts, on the east wall north of the desk, where the guests who
     * check in walk to. Drawn on the wall: two steel doors in a bronze
     * surround, the split down each, the call button between them and a lit
     * floor indicator over each. Nothing here is a box anybody stands in.
     */
    const steel = paint(0xa7adb3, { roughness: 0.32, metalness: 0.75 });
    const bronze = paint(0x6b5233, { roughness: 0.45, metalness: 0.55 });
    const seam = paint(0x2d3034, { roughness: 0.6 });
    const lit = paint(0xffd58a, { roughness: 0.4, emissive: 0xffb84a, emissiveIntensity: 0.9 });
    for (const z of [m(16), m(23)]) {
      b.panel(bronze, wallX - 0.012, m(4.3), z, m(5.4), m(8.6), [-1, 0]);
      b.panel(steel, wallX - 0.02, m(3.9), z, m(4.4), m(7.8), [-1, 0]);
      b.panel(seam, wallX - 0.024, m(3.9), z, 0.012, m(7.8), [-1, 0]);
      b.panel(lit, wallX - 0.02, m(8.9), z, m(1.6), m(0.45), [-1, 0]);
    }
    b.panel(bronze, wallX - 0.02, m(4), m(19.5), m(0.5), m(0.9), [-1, 0]);
    b.panel(lit, wallX - 0.024, m(4.1), m(19.5), m(0.22), m(0.22), [-1, 0]);
    // A rug under the lounge seating.
    const seats = plan.props.filter((q) => /lobby-seat/.test(q.id)).map(box);
    if (seats.length) {
      const minX = Math.min(...seats.map((s) => s.minX)) - 0.8;
      const maxX = Math.max(...seats.map((s) => s.maxX)) + 0.8;
      const minZ = Math.min(...seats.map((s) => s.minZ)) - 0.8;
      const maxZ = Math.max(...seats.map((s) => s.maxZ)) + 0.8;
      b.decal(paint(0x6a2a2e, { roughness: 1 }), (minX + maxX) / 2, (minZ + maxZ) / 2, maxX - minX, maxZ - minZ, 0.008);
      b.decal(paint(0xc8a45a, { roughness: 1 }), (minX + maxX) / 2, (minZ + maxZ) / 2, maxX - minX - 0.5, maxZ - minZ - 0.5, 0.009);
      b.decal(paint(0x6a2a2e, { roughness: 1 }), (minX + maxX) / 2, (minZ + maxZ) / 2, maxX - minX - 0.7, maxZ - minZ - 0.7, 0.01);
    }
  }

  atlas.finish();
  const meshes = b.build(group);

  return {
    object3D: group,
    meshes,
    /** The time clock keeps the time it is handed. */
    update({ minutes }) {
      if (!hands) return;
      const h = ((minutes / 60) % 12) / 12;
      const mm = (minutes % 60) / 60;
      // Seen from the front, looking west, clockwise runs toward -z: a negative
      // turn about +x.
      hands.hour.rotation.x = -h * Math.PI * 2;
      hands.minute.rotation.x = -mm * Math.PI * 2;
    },
    dispose() {
      for (const mesh of meshes) mesh.geometry.dispose();
      for (const d of disposables) d.dispose();
    },
  };
}
